import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { SkeletonModule } from "primeng/skeleton";
import { TagModule } from "primeng/tag";

// Our services
import { CustomersService } from "../../services/customers.service";
import { SessionService } from "../../../../core/services/session.service";
import { QPilotAccessToken } from "../../../../core/services/qpilot.service";

// Our interfaces
import {
  CustomerHistoryDialogData,
  CustomerHistoryEntry,
  EventOriginatorType,
} from "../../interfaces/customer-history.interface";

/**
 * Customer History Dialog Component
 * Displays customer activity timeline in a modal
 */

@Component({
  selector: "app-customer-history-dialog",
  standalone: true,
  imports: [CommonModule, SkeletonModule, TagModule],
  templateUrl: "./customer-history-dialog.component.html",
  styleUrl: "./customer-history-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerHistoryDialogComponent implements OnInit, OnDestroy {
  private config = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private customersService = inject(CustomersService);
  private sessionService = inject(SessionService);

  // State
  private allHistory = signal<CustomerHistoryEntry[]>([]);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  // Filter state
  selectedFilter = signal<string>("all");

  // Computed stats for filter badges
  // Note: Count System entries explicitly, all others are User
  // This ensures all + user + system adds up correctly
  filterStats = computed(() => {
    const all = this.allHistory();
    const systemCount = all.filter(
      (entry) => entry.eventOriginatorType === EventOriginatorType.System,
    ).length;
    // User count = everything that's NOT system (includes undefined/null)
    const userCount = all.length - systemCount;
    return {
      all: all.length,
      user: userCount,
      system: systemCount,
    };
  });

  // Computed filtered history
  // User = everything that's NOT system (matches filterStats logic)
  history = computed(() => {
    const all = this.allHistory();
    const filter = this.selectedFilter();

    if (filter === "user") {
      // User = everything that's NOT system (includes undefined/null)
      return all.filter(
        (entry) => entry.eventOriginatorType !== EventOriginatorType.System,
      );
    } else if (filter === "system") {
      return all.filter(
        (entry) => entry.eventOriginatorType === EventOriginatorType.System,
      );
    }
    return all;
  });

  private destroy$ = new Subject<void>();

  /**
   * Toggle filter (if already selected, reset to 'all')
   */
  toggleFilter(value: string): void {
    if (this.selectedFilter() === value) {
      this.selectedFilter.set("all");
    } else {
      this.selectedFilter.set(value);
    }
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    const data = this.config.data as CustomerHistoryDialogData;
    if (data?.embeddedMode && data?.inputToken) {
      return data.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Load customer history from API
   */
  private loadHistory(): void {
    const data = this.config.data as CustomerHistoryDialogData;
    if (!data?.customerId) {
      this.error.set("No customer ID provided.");
      this.loading.set(false);
      return;
    }

    const accessToken = this.getAccessToken();
    this.customersService
      .getCustomerHistory(data.customerId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          this.allHistory.set(entries);
          this.loading.set(false);
        },
        error: (err) => {
          console.error("Failed to load history:", err);
          this.error.set("Failed to load customer history.");
          this.loading.set(false);
        },
      });
  }

  /**
   * Check if entry is an error (has exception)
   */
  isError(entry: CustomerHistoryEntry): boolean {
    return entry.exceptionType !== "None" && entry.exceptionType !== "";
  }

  /**
   * Check if entry was triggered by system (QPilot) vs user
   */
  isSystemOriginator(entry: CustomerHistoryEntry): boolean {
    return entry.eventOriginatorType === EventOriginatorType.System;
  }

  /**
   * Get icon based on exception type and originator
   * - Error: pi-exclamation-circle (red)
   * - System (QPilot): pi-cog (blue)
   * - User: pi-user (green)
   */
  getEntryIcon(entry: CustomerHistoryEntry): string {
    if (this.isError(entry)) {
      return "pi pi-exclamation-circle";
    }
    if (this.isSystemOriginator(entry)) {
      return "pi pi-cog";
    }
    return "pi pi-user";
  }

  /**
   * Get icon color based on entry type
   */
  getEntryColor(entry: CustomerHistoryEntry): string {
    if (this.isError(entry)) {
      return "#ef4444"; // red for errors
    }
    if (this.isSystemOriginator(entry)) {
      return "#3b82f6"; // blue for system/QPilot
    }
    return "#22c55e"; // green for user actions
  }

  /**
   * Get severity for tag
   */
  getEntrySeverity(
    entry: CustomerHistoryEntry,
  ): "success" | "info" | "warn" | "danger" | "secondary" {
    if (this.isError(entry)) {
      return "danger";
    }
    if (this.isSystemOriginator(entry)) {
      return "info";
    }
    return "success";
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | string | null | undefined): string {
    if (!date) return "-";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "-";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Get tooltip text showing who triggered the event
   */
  getTooltip(entry: CustomerHistoryEntry): string {
    return `${entry.eventVerb} by ${entry.originatorFormatted}`;
  }

  /**
   * Close dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}
